/**
 * EventBus — Singleton typed event bus
 *
 * Central hub for all system events. Supports:
 * - Typed subscriptions by event type or wildcard ('*')
 * - Async sequential handler execution
 * - Circular buffer history (last 200 events)
 * - Pause/resume for bulk operations
 */

import type { EngineEvent, EngineEventType, EngineEventHandler, EventHistoryFilter } from './types';

const HISTORY_BUFFER_SIZE = 200;

class EventBus {
  private handlers: Map<string, Set<EngineEventHandler>> = new Map();
  private history: EngineEvent[] = [];
  private paused = false;
  private queuedWhilePaused: EngineEvent[] = [];

  /**
   * Subscribe to an event type or '*' for all events.
   * Returns an unsubscribe function.
   */
  on(eventType: EngineEventType | '*', handler: EngineEventHandler): () => void {
    const key = eventType;
    if (!this.handlers.has(key)) {
      this.handlers.set(key, new Set());
    }
    this.handlers.get(key)!.add(handler);

    return () => this.off(eventType, handler);
  }

  /**
   * Remove a handler for a specific event type
   */
  off(eventType: EngineEventType | '*', handler: EngineEventHandler): void {
    const set = this.handlers.get(eventType);
    if (set) {
      set.delete(handler);
      if (set.size === 0) {
        this.handlers.delete(eventType);
      }
    }
  }

  /**
   * Emit an event. Handlers are executed sequentially (async-safe).
   * If paused, events are queued and replayed on resume.
   */
  async emit(event: EngineEvent): Promise<void> {
    // Add to circular history buffer
    this.history.push(event);
    if (this.history.length > HISTORY_BUFFER_SIZE) {
      this.history.shift();
    }

    if (this.paused) {
      this.queuedWhilePaused.push(event);
      return;
    }

    await this.dispatch(event);
  }

  /**
   * Get event history with optional filtering
   */
  getHistory(filter?: EventHistoryFilter): EngineEvent[] {
    let events = [...this.history];

    if (filter) {
      if (filter.type) {
        events = events.filter((e) => e.type === filter.type);
      }
      if (filter.entityType) {
        events = events.filter((e) => e.entityType === filter.entityType);
      }
      if (filter.source) {
        events = events.filter((e) => e.source === filter.source);
      }
      if (filter.since) {
        const sinceTime = new Date(filter.since).getTime();
        events = events.filter((e) => new Date(e.timestamp).getTime() >= sinceTime);
      }
      if (filter.limit && filter.limit > 0) {
        events = events.slice(-filter.limit);
      }
    }

    return events;
  }

  /**
   * Pause event dispatching. Events are buffered and replayed on resume.
   */
  pause(): void {
    this.paused = true;
  }

  /**
   * Resume event dispatching. Replays any events buffered during pause.
   */
  async resume(): Promise<void> {
    this.paused = false;
    const queued = [...this.queuedWhilePaused];
    this.queuedWhilePaused = [];

    for (const event of queued) {
      await this.dispatch(event);
    }
  }

  /**
   * Check if the bus is currently paused
   */
  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Remove all handlers and clear history
   */
  reset(): void {
    this.handlers.clear();
    this.history = [];
    this.queuedWhilePaused = [];
    this.paused = false;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private async dispatch(event: EngineEvent): Promise<void> {
    // Collect handlers: specific type + wildcard
    const specificHandlers = this.handlers.get(event.type);
    const wildcardHandlers = this.handlers.get('*');

    const allHandlers: EngineEventHandler[] = [];
    if (specificHandlers) allHandlers.push(...specificHandlers);
    if (wildcardHandlers) allHandlers.push(...wildcardHandlers);

    for (const handler of allHandlers) {
      try {
        await handler(event);
      } catch (err) {
        console.error(`[EventBus] Handler error for event "${event.type}":`, err);
      }
    }
  }
}

/**
 * Singleton EventBus instance
 */
export const eventBus = new EventBus();
